import { api, nullOn404, on404 } from 'shared/api/client';
import ServerErrorHandler from 'shared/feedback/ErrorHandler';
import { useQueryClient, useMutation, useQuery, UseQueryResult, UseMutationResult } from '@tanstack/react-query';
import { Unit, CreateUnitInput, UpdateUnitInput, DeleteUnitInput, ExistsResponse, SuccessResponse, UUID } from './types';

export const unitQueryKeys = {
  unit: 'UNIT',
  unitExists: 'UNIT_EXISTS',
  unitsByDocument: 'UNITS_BY_DOCUMENT',
} as const;

export function useUnit(unitId: UUID | undefined): UseQueryResult<Unit | null> {
  return useQuery({
    queryKey: [unitQueryKeys.unit, unitId],

    queryFn: () =>
      api
        .GET('/units/{unitId}', { params: { path: { unitId: unitId as string } } })
        .then((res) => res.data)
        .catch(nullOn404),

    enabled: !!unitId,
  });
}

export function useUnitExists(unitId: UUID | undefined): UseQueryResult<ExistsResponse> {
  return useQuery({
    queryKey: [unitQueryKeys.unitExists, unitId],

    queryFn: () =>
      api
        .GET('/units/{unitId}/exists', { params: { path: { unitId: unitId as string } } })
        .then((res) => res.data)
        .catch(on404({ exists: false })),

    enabled: !!unitId,
  });
}

export function useUnitsByDocument(documentSuperId: number | undefined): UseQueryResult<Unit[]> {
  return useQuery({
    queryKey: [unitQueryKeys.unitsByDocument, documentSuperId],

    queryFn: () =>
      api
        .GET('/units/document/{documentSuperId}', { params: { path: { documentSuperId: String(documentSuperId) } } })
        .then((res) => res.data)
        .catch(on404([])),

    enabled: !!documentSuperId,
  });
}

export function useCreateUnit(): UseMutationResult<Unit, unknown, CreateUnitInput> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (unit: CreateUnitInput) => {
      const res = await api.POST('/units/', { body: unit });
      return res?.data;
    },

    onError: ServerErrorHandler,

    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: [unitQueryKeys.unitsByDocument, variables.documentSuperId],
      });
    },
  });
}

export function useUpdateUnit(): UseMutationResult<SuccessResponse, unknown, UpdateUnitInput> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ unitId, ...unitData }: UpdateUnitInput) => {
      const res = await api.PUT('/units/{unitId}', { params: { path: { unitId } }, body: unitData });
      return res?.data;
    },

    onError: ServerErrorHandler,

    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: [unitQueryKeys.unit, variables.unitId],
      });
      if (variables.documentSuperId) {
        queryClient.invalidateQueries({
          queryKey: [unitQueryKeys.unitsByDocument, variables.documentSuperId],
        });
      }
    },
  });
}

export function useDeleteUnit(): UseMutationResult<SuccessResponse, unknown, DeleteUnitInput> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ unitId }: DeleteUnitInput) => {
      const res = await api.DELETE('/units/{unitId}', { params: { path: { unitId } } });
      return res?.data;
    },

    onError: ServerErrorHandler,

    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: [unitQueryKeys.unit, variables.unitId],
      });
      if (variables.documentSuperId) {
        queryClient.invalidateQueries({
          queryKey: [unitQueryKeys.unitsByDocument, variables.documentSuperId],
        });
      }
    },
  });
}
